"use client";

import React, { useState, useEffect, useRef } from "react";
import { Move } from "lucide-react";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { getWithAuth, postWithAuth } from "@/lib/api-helpers";
import ImageCropModal from "@/components/tailwind/ui/ImageCropModal";
import { useImageCropUpload } from "@/hooks/useImageCropUpload";
import { SmartImage } from "@/components/tailwind/ui/SmartImage";

interface CoverImageProps {
  coverUrl: string | null;
  onCoverChange: (url: string) => void;
  onCoverRemove: () => void;
  onUploadCover?: (file: File) => Promise<string>;
  workspaceId?: string;
  openPicker?: boolean;
  onPickerClose?: () => void;
  /** Crop uploaded covers before saving. Default true. */
  enableCrop?: boolean;
  cropAspect?: number;
  cropShape?: "rect" | "round";
  /** Saved focal point (0–100). Absent ⇒ centered. */
  coverPosition?: { x: number; y: number } | null;
  /** Persist a new focal point. Providing this enables the Reposition affordance. */
  onCoverPositionChange?: (position: { x: number; y: number }) => void;
}

interface CoverImage {
  url: string;
  name: string;
  position: string;
}

interface CoverCategory {
  name: string;
  link: string;
  images: CoverImage[];
}

/** One photo from GET /api/cover/unsplash. Mirrors the API's response shape. */
interface UnsplashPhoto {
  id: string;
  thumb: string;
  full: string;
  alt: string;
  color: string;
  user: { name: string; link: string };
  downloadLocation: string;
}

interface UnsplashSearchResponse {
  photos: UnsplashPhoto[];
  hasMore: boolean;
}

export const coverImages = [
  {
    name: "webb9",
    url: "https://raw.githubusercontent.com/bq-praveen-33/novel-data/main/docs/notes/68ff105b39ec3d06c5436af1/images/webb9.jpg-16700a4d-2002-4bb5-9a58-093f846f4891",
  },
  {
    name: "webb10",
    url: "https://raw.githubusercontent.com/bq-praveen-33/novel-data/main/docs/notes/68ff105b39ec3d06c5436af1/images/webb10.jpg-b2ed6e01-04fa-4aa5-9348-2e174bced6d1",
  },
  {
    name: "webb11",
    url: "https://raw.githubusercontent.com/bq-praveen-33/novel-data/main/docs/notes/68ff105b39ec3d06c5436af1/images/webb11.jpg-74b82ff6-4c24-4624-b9b5-271018338cc5",
  },
  {
    name: "webb8",
    url: "https://raw.githubusercontent.com/bq-praveen-33/novel-data/main/docs/notes/68ff105b39ec3d06c5436af1/images/webb8.jpg-fc5e1f03-2e45-4ec4-b42c-ceea68e0914a",
  },
  {
    name: "webb7",
    url: "https://raw.githubusercontent.com/bq-praveen-33/novel-data/main/docs/notes/68ff105b39ec3d06c5436af1/images/webb7.jpg-1a8679f3-4615-4238-be82-ca7eeabb564a",
  },
  {
    name: "webb5",
    url: "https://raw.githubusercontent.com/bq-praveen-33/novel-data/main/docs/notes/68ff105b39ec3d06c5436af1/images/webb5.jpg-6e6e3edd-b5bc-4358-9057-882615687ec9",
  },
  {
    name: "webb4",
    url: "https://raw.githubusercontent.com/bq-praveen-33/novel-data/main/docs/notes/68ff105b39ec3d06c5436af1/images/webb4.jpg-f4956ea4-3be4-4038-ab37-13c443b1f747",
  },
  {
    name: "webb3",
    url: "https://raw.githubusercontent.com/bq-praveen-33/novel-data/main/docs/notes/68ff105b39ec3d06c5436af1/images/webb3.jpg-c5fa84c9-eb10-45c0-ada0-d942c375505e",
  },
  {
    name: "webb2",
    url: "https://raw.githubusercontent.com/bq-praveen-33/novel-data/main/docs/notes/68ff105b39ec3d06c5436af1/images/webb2.jpg-a5d3c718-d396-4c0e-9261-fc686a9f83a7",
  },
  {
    name: "webb1",
    url: "https://raw.githubusercontent.com/bq-praveen-33/novel-data/main/docs/notes/68ff105b39ec3d06c5436af1/images/webb1.jpg-511f13da-cd30-4431-acfd-840b180d9b72",
  },
];

export default function CoverImage({ coverUrl, onCoverChange, onCoverRemove, onUploadCover, workspaceId, openPicker, onPickerClose, enableCrop = true, cropAspect = 16 / 9, cropShape = "rect", coverPosition, onCoverPositionChange }: CoverImageProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [activeTab, setActiveTab] = useState<"gallery" | "upload" | "link" | "unsplash">("gallery");
  const [coverCategories, setCoverCategories] = useState<CoverCategory[]>([]);
  const [uploadedCovers, setUploadedCovers] = useState<CoverImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [linkValue, setLinkValue] = useState("");

  // ── Unsplash tab ──────────────────────────────────────────
  // Live search through /api/cover/unsplash, which proxies Unsplash so the
  // access key stays on the server. An empty query returns their popular feed.
  const [unsplashQuery, setUnsplashQuery] = useState("");
  const [unsplashPhotos, setUnsplashPhotos] = useState<UnsplashPhoto[]>([]);
  const [unsplashLoading, setUnsplashLoading] = useState(false);
  const [unsplashError, setUnsplashError] = useState<string | null>(null);
  const debouncedUnsplashQuery = useDebouncedValue(unsplashQuery, 350);
  const unsplashRequestRef = useRef(0);
  // On phones, render the cover picker as a bottom sheet
  // instead of a centered modal — easier thumb reach and consistent with
  // EmojiPicker / EditorOptionsMenu / Breadcrumb hierarchy on mobile.
  const isMobile = useIsMobile();

  // ── Cover reposition (drag-to-frame) ──────────────────────
  // Enabled only when the host editor passes a persist callback. Drag the
  // banner vertically to choose which slice of the cover-fit image shows,
  // then Save — the focal point persists as a {x,y} sibling on the page value.
  const canReposition = Boolean(onCoverPositionChange);
  const savedPosition = coverPosition ?? { x: 50, y: 50 };
  const [isRepositioning, setIsRepositioning] = useState(false);
  const [draftPosition, setDraftPosition] = useState<{ x: number; y: number } | null>(null);
  const dragState = useRef<{ startY: number; startFocalY: number; height: number } | null>(null);
  const effectivePosition = draftPosition ?? savedPosition;

  const startReposition = () => {
    setDraftPosition(savedPosition);
    setIsRepositioning(true);
  };
  const cancelReposition = () => {
    setDraftPosition(null);
    setIsRepositioning(false);
  };
  const saveReposition = () => {
    if (draftPosition && onCoverPositionChange) onCoverPositionChange(draftPosition);
    setDraftPosition(null);
    setIsRepositioning(false);
  };

  const handleRepositionPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragState.current = {
      startY: e.clientY,
      startFocalY: (draftPosition ?? savedPosition).y,
      height: rect.height || 1,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const handleRepositionPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const st = dragState.current;
    if (!st) return;
    const dy = e.clientY - st.startY;
    // Drag down → reveal the upper part of the image → focal y decreases.
    const nextY = Math.max(0, Math.min(100, st.startFocalY - (dy / st.height) * 100));
    setDraftPosition({ x: 50, y: Math.round(nextY) });
  };
  const handleRepositionPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    dragState.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  };

  // Handle external picker control
  useEffect(() => {
    if (openPicker !== undefined) {
      setShowPicker(openPicker);
    }
  }, [openPicker]);

  // Load cover images dynamically from API
  useEffect(() => {
    const loadCovers = async () => {
      try {
        // Always show local covers immediately
        const wsId = workspaceId || 'default';
        const uploadData = coverImages;
        if (uploadData && uploadData.length > 0) {
          setUploadedCovers(
            uploadData.map((cover: any) => ({
              url: cover.url,
              name: cover.name,
              position: 'center 50%'
            }))
          );
        }

        // Load gallery covers (best effort; do not block local list)
        // try {
        //   const response = await fetch('/api/covers');
        //   if (!response.ok) throw new Error('Failed to fetch covers');
        //   const data = await response.json();

        //   if (data.images && data.images.length > 0) {
        //     const categories: CoverCategory[] = [];

        //     const webbImages = data.images.filter((img: CoverImage) =>
        //       img.name.toLowerCase().startsWith('webb')
        //     );

        //     if (webbImages.length > 0) {
        //       categories.push({
        //         name: "James Webb Telescope",
        //         link: "https://webbtelescope.org/",
        //         images: webbImages.map((img: CoverImage, idx: number) => ({
        //           ...img,
        //           position:
        //             idx % 4 === 0 ? 'center 0%' :
        //             idx % 4 === 1 ? 'center 30%' :
        //             idx % 4 === 2 ? 'center 50%' : 'center 60%'
        //         }))
        //       });
        //     }

        //     const otherImages = data.images.filter((img: CoverImage) =>
        //       !img.name.toLowerCase().startsWith('webb')
        //     );

        //     if (otherImages.length > 0) {
        //       categories.push({
        //         name: "Other Covers",
        //         link: "#",
        //         images: otherImages
        //       });
        //     }

        //     setCoverCategories(categories);
        //   }
        // } catch (err) {
        //   // Silently ignore; local covers are already shown
        // }
      } catch (error) {
        console.error('Failed to load cover images:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (showPicker) {
      loadCovers();
    }
  }, [showPicker, workspaceId]);

  const handlePickerClose = () => {
    setShowPicker(false);
    if (onPickerClose) {
      onPickerClose();
    }
  };

  const fetchUnsplash = async () => {
    // Stamp each request so a slow earlier one can't overwrite a newer result.
    const requestId = ++unsplashRequestRef.current;

    setUnsplashLoading(true);
    setUnsplashError(null);

    const params = new URLSearchParams({ page: "1" });
    if (debouncedUnsplashQuery.trim()) params.set("query", debouncedUnsplashQuery.trim());

    const res = await getWithAuth<UnsplashSearchResponse>(`/api/cover/unsplash?${params}`);

    if (requestId !== unsplashRequestRef.current) return;

    if (res && typeof res === "object" && "isError" in res) {
      setUnsplashError(
        res.status === 501
          ? "Unsplash isn't set up yet — add UNSPLASH_ACCESS_KEY on the server."
          : res.message || "Couldn't reach Unsplash.",
      );
      setUnsplashPhotos([]);
    } else {
      setUnsplashPhotos((res as UnsplashSearchResponse).photos);
    }

    setUnsplashLoading(false);
  };

  // Re-search whenever the tab opens or the typed query settles.
  useEffect(() => {
    if (!showPicker || activeTab !== "unsplash") return;
    void fetchUnsplash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPicker, activeTab, debouncedUnsplashQuery]);

  /** Applies a photo, pinging the download endpoint Unsplash's terms require. */
  const chooseUnsplashPhoto = (photo: UnsplashPhoto) => {
    onCoverChange(photo.full);
    handlePickerClose();
    if (photo.downloadLocation) {
      void postWithAuth("/api/cover/unsplash/download", {
        downloadLocation: photo.downloadLocation,
      }).catch(() => {
        /* tracking is best-effort; the cover is already applied */
      });
    }
  };

  const coverCrop = useImageCropUpload({
    aspect: cropAspect,
    cropShape,
    title: "Crop cover image",
    fileName: "cover.jpg",
    output: { maxSize: 1600 },
    upload: async (file) => {
      if (onUploadCover) return await onUploadCover(file);
      // No uploader provided: fall back to an inline data URL.
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
    },
    onUploaded: (url) => {
      onCoverChange(url);
      handlePickerClose();
    },
  });

  const coverUploading = isUploading || coverCrop.isUploading;

  const uploadCoverDirect = async (file: File) => {
    setIsUploading(true);
    try {
      if (onUploadCover) {
        const uploadedUrl = await onUploadCover(file);
        onCoverChange(uploadedUrl);
      } else {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target?.result) {
            onCoverChange(event.target.result as string);
            handlePickerClose();
          }
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setIsUploading(false);
      handlePickerClose();
    }
  };

  // Paste: ⌘/Ctrl+V an image while the Upload tab is open.
  useEffect(() => {
    if (!showPicker || activeTab !== "upload") return;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageItem = Array.from(items).find((it) => it.type.startsWith("image/"));
      const file = imageItem?.getAsFile();
      if (!file) return;
      e.preventDefault();
      if (enableCrop) coverCrop.openCropper(file);
      else void uploadCoverDirect(file);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPicker, activeTab, enableCrop]);

  return (
    <>
      {/* Only show cover image if coverUrl exists */}
      {coverUrl && (
        <div data-page-cover className="group relative w-full h-[30vh] max-h-[280px] flex-shrink-0">
          <div
            className={`w-full h-full ${isRepositioning ? "cursor-grab active:cursor-grabbing select-none" : "cursor-pointer"}`}
            style={isRepositioning ? { touchAction: "none" } : undefined}
            // While repositioning, the banner becomes a vertical drag surface.
            // Otherwise: mobile single-tap / desktop double-click opens the picker
            // (onDoubleClick is unreliable on touch; iOS intercepts it for zoom).
            {...(isRepositioning
              ? {
                onPointerDown: handleRepositionPointerDown,
                onPointerMove: handleRepositionPointerMove,
                onPointerUp: handleRepositionPointerUp,
                onPointerCancel: handleRepositionPointerUp,
              }
              : isMobile
                ? { onClick: () => setShowPicker(true) }
                : { onDoubleClick: () => setShowPicker(true) })}
          >
            {/* Banner: cover-fit, never distorted. focalPoint = the saved
                reposition value, live-updated while dragging. */}
            <SmartImage
              src={coverUrl}
              alt="Page cover"
              fit="cover"
              focalPoint={effectivePosition}
              sizes="100vw"
              priority
              className="h-full w-full"
            />
          </div>

          {/* "Reposition" entry — hover-revealed on desktop, always shown on
              mobile; only when the editor wired a persist callback. */}
          {canReposition && !isRepositioning && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); startReposition(); }}
              className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 rounded-md bg-white/85 dark:bg-zinc-900/85 px-2.5 py-1 text-xs font-medium text-zinc-700 dark:text-zinc-200 shadow-sm ring-1 ring-black/5 dark:ring-white/10 backdrop-blur transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100"
            >
              <Move className="h-3.5 w-3.5" />
              Reposition
            </button>
          )}

          {/* Reposition mode: focus ring + drag hint + Save / Cancel. */}
          {isRepositioning && (
            <>
              <div className="pointer-events-none absolute inset-0 ring-2 ring-inset ring-blue-500" />
              <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-lg bg-zinc-900/90 px-3 py-1.5 text-xs text-white shadow-lg backdrop-blur">
                <span className="hidden opacity-80 sm:inline">Drag the image to reposition</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); cancelReposition(); }}
                  className="rounded px-2 py-0.5 transition-colors hover-component"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); saveReposition(); }}
                  className="rounded bg-blue-500 px-2 py-0.5 font-medium transition-colors hover:bg-blue-700"
                >
                  Save position
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Cover Picker Modal — centered modal on desktop, bottom sheet on
          mobile. The wrapper's flex alignment and inner dimensions both
          branch on `isMobile`; desktop behavior is preserved exactly. */}
      {showPicker && (
        <div
          className={
            isMobile
              ? "fixed inset-0 z-[250] flex items-end justify-center bg-black/40"
              : "fixed inset-0 z-[250] flex items-start justify-center pt-20 bg-black/20 dark:bg-black/40"
          }
          onClick={handlePickerClose}
        >
          <div
            className={
              isMobile
                ? "bg-white dark:bg-zinc-900 rounded-t-lg shadow-2xl overflow-hidden w-full flex flex-col"
                : "bg-white dark:bg-zinc-900 rounded-lg shadow-2xl overflow-hidden w-[540px] max-w-[calc(100vw-24px)] flex flex-col"
            }
            style={isMobile ? { maxHeight: "min(85dvh, 600px)" } : { maxHeight: "485px" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Tabs Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 dark:border-zinc-800 px-2 overflow-x-auto">
              <div className="flex gap-1 py-1.5">
                <button
                  type="button"
                  title="Choose from gallery"
                  onClick={() => setActiveTab("gallery")}
                  className={`relative px-2 h-7 rounded-md text-sm transition-colors whitespace-nowrap ${activeTab === "gallery"
                    ? "text-gray-900 dark:text-white"
                    : "text-gray-600 dark:text-gray-400 hover-component "
                    }`}
                >
                  Gallery
                  {activeTab === "gallery" && (
                    <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-gray-900 dark:bg-white" />
                  )}
                </button>
                <button
                  type="button"
                  title="Upload an image"
                  onClick={() => setActiveTab("upload")}
                  // `relative` so the underline indicator below can absolute-
                  // position against this button — without it the indicator
                  // would anchor against the tabs container and look misaligned.
                  className={`relative px-2 h-7 rounded-md text-sm transition-colors whitespace-nowrap ${activeTab === "upload"
                    ? "text-gray-900 dark:text-white"
                    : "text-gray-600 dark:text-gray-400 hover-component "
                    }`}
                >
                  Upload
                  {activeTab === "upload" && (
                    <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-gray-900 dark:bg-white" />
                  )}
                </button>
                <button
                  type="button"
                  title="Use an image link"
                  onClick={() => setActiveTab("link")}
                  className={`relative px-2 h-7 rounded-md text-sm transition-colors whitespace-nowrap ${activeTab === "link"
                    ? "text-gray-900 dark:text-white"
                    : "text-gray-600 dark:text-gray-400 hover-component "
                    }`}
                >
                  Link
                  {activeTab === "link" && (
                    <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-gray-900 dark:bg-white" />
                  )}
                </button>
                <button
                  type="button"
                  title="Search Unsplash"
                  onClick={() => setActiveTab("unsplash")}
                  className={`relative flex items-center gap-1.5 px-2 h-7 rounded-md text-sm transition-colors whitespace-nowrap ${activeTab === "unsplash"
                    ? "text-gray-900 dark:text-white"
                    : "text-gray-600 dark:text-gray-400 hover-component "
                    }`}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3" aria-hidden="true">
                    <path d="M7.5 6.75V0h9v6.75h-9zm9 3.75H24V24H0V10.5h7.5v6.75h9V10.5z" />
                  </svg>
                  Unsplash
                  {activeTab === "unsplash" && (
                    <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-gray-900 dark:bg-white" />
                  )}
                </button>
              </div>
              <button
                type="button"
                title="Remove cover image"
                onClick={() => {
                  onCoverRemove();
                  handlePickerClose();
                }}
                className="px-2 h-7 rounded-md text-sm text-gray-600 dark:text-gray-400 hover-component  transition-colors"
              >
                Remove
              </button>
            </div>

            {/* Gallery Tab Content */}
            {activeTab === "gallery" && (
              <div className="flex-1 overflow-y-auto pb-2">
                {isLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Loading covers...
                    </div>
                  </div>
                ) : coverCategories.length === 0 && uploadedCovers.length === 0 ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        No cover images found
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        Add images to <code className="bg-gray-100 dark:bg-zinc-800 px-1 rounded">public/images/page-cover/</code>
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Uploaded Covers Section */}
                    {uploadedCovers.length > 0 && (
                      <div className="py-1 px-1">
                        {/* Category Header */}
                        <div className="flex items-center justify-between px-2 mt-1.5 mb-2">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            Uploaded Covers
                          </span>
                        </div>

                        {/* Image Grid */}
                        <div className="flex flex-wrap px-3">
                          {uploadedCovers.map((image, idx) => (
                            <div key={`uploaded-${idx}`} className="w-1/4 p-[3px]">
                              <button
                                type="button"
                                onClick={() => {
                                  onCoverChange(image.url);
                                  handlePickerClose();
                                }}
                                className={`w-full h-16 rounded overflow-hidden transition-all ${coverUrl === image.url
                                  ? "ring-2 ring-blue-500"
                                  : "hover:opacity-80"
                                  }`}
                              >
                                <img
                                  src={image.url}
                                  alt={image.name}
                                  className="w-full h-full object-cover"
                                  style={{ objectPosition: image.position }}
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Gallery Covers */}
                    {coverCategories.map((category) => (
                      <div key={category.name} className="py-1 px-1">
                        {/* Category Header */}
                        <div className="flex items-center justify-between px-2 mt-1.5 mb-2">
                          <a
                            href={category.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                          >
                            {category.name}
                          </a>
                        </div>

                        {/* Image Grid */}
                        <div className="flex flex-wrap px-3">
                          {category.images.map((image, idx) => (
                            <div key={image.url} className="w-1/4 p-[3px]">
                              <button
                                type="button"
                                onClick={() => {
                                  onCoverChange(image.url);
                                  handlePickerClose();
                                }}
                                className={`w-full h-16 rounded overflow-hidden transition-all ${coverUrl === image.url
                                  ? "ring-2 ring-blue-500"
                                  : "hover:opacity-80"
                                  }`}
                              >
                                <img
                                  src={image.url}
                                  alt={image.name}
                                  className="w-full h-full object-cover"
                                  style={{ objectPosition: image.position }}
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Upload Tab Content — full-width outlined button +
                paste hint + size hint. */}
            {activeTab === "upload" && (
              <div className="flex-1 flex flex-col items-center justify-center gap-2.5 px-4 py-8">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="cover-upload"
                  disabled={coverUploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (enableCrop) {
                        coverCrop.openCropper(file);
                      } else {
                        void uploadCoverDirect(file);
                      }
                    }
                    // Clear input to allow re-selecting the same file if needed
                    if (e.target instanceof HTMLInputElement) e.target.value = "";
                  }}
                />
                <label
                  htmlFor="cover-upload"
                  className={`w-full rounded-md border px-3 py-1.5 text-center text-sm font-medium transition-colors cursor-pointer border-gray-300 text-gray-700 hover-component dark:border-zinc-700 dark:text-gray-200  ${coverUploading ? "cursor-not-allowed opacity-60" : ""}`}
                  aria-disabled={coverUploading}
                >
                  {coverUploading ? "Uploading…" : "Upload file"}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  or ⌘+V to paste an image
                </p>
                {/* <p className="text-[11px] text-gray-400 dark:text-gray-600">
                  Images wider than 1500 pixels work best.
                </p> */}
              </div>
            )}

            {/* Link Tab Content — full-width input + Submit + hint. */}
            {activeTab === "link" && (
              <div className="flex-1 flex flex-col items-center justify-center gap-2.5 px-4 py-8">
                <input
                  type="text"
                  autoFocus
                  value={linkValue}
                  placeholder="Paste an image link…"
                  onChange={(e) => setLinkValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && linkValue.trim()) {
                      onCoverChange(linkValue.trim());
                      handlePickerClose();
                      setLinkValue("");
                    }
                  }}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder-faint focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
                />
                <button
                  type="button"
                  disabled={!linkValue.trim()}
                  onClick={() => {
                    if (!linkValue.trim()) return;
                    onCoverChange(linkValue.trim());
                    handlePickerClose();
                    setLinkValue("");
                  }}
                  className="w-full rounded-md bg-blue-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Submit
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  Works with any image from the web.
                </p>
              </div>
            )}

            {/* Unsplash Tab Content — search box over the same 4-up grid the
                gallery uses. Each photo credits its author, as Unsplash requires. */}
            {activeTab === "unsplash" && (
              <div className="flex-1 overflow-y-auto">
                <div className="sticky top-0 z-10 bg-white px-3 pt-3 pb-2 dark:bg-zinc-900">
                  <input
                    type="text"
                    autoFocus
                    value={unsplashQuery}
                    placeholder="Search for an image…"
                    onChange={(e) => setUnsplashQuery(e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder-faint focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
                  />
                </div>

                {unsplashError ? (
                  <div className="flex h-56 items-center justify-center px-6 text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">{unsplashError}</p>
                  </div>
                ) : unsplashLoading ? (
                  <div className="flex h-56 items-center justify-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Searching…</p>
                  </div>
                ) : unsplashPhotos.length === 0 ? (
                  <div className="flex h-56 items-center justify-center px-6 text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {debouncedUnsplashQuery.trim()
                        ? `No photos found for “${debouncedUnsplashQuery.trim()}”.`
                        : "No photos to show."}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap px-3">
                      {unsplashPhotos.map((photo) => (
                        <div key={photo.id} className="w-1/4 p-[3px]">
                          <button
                            type="button"
                            onClick={() => chooseUnsplashPhoto(photo)}
                            className={`w-full h-16 rounded overflow-hidden transition-all ${coverUrl === photo.full
                              ? "ring-2 ring-blue-500"
                              : "hover:opacity-80"
                              }`}
                            style={{ backgroundColor: photo.color }}
                          >
                            <img
                              src={photo.thumb}
                              alt={photo.alt}
                              loading="lazy"
                              className="w-full h-full object-cover"
                            />
                          </button>
                          <a
                            href={photo.user.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-0.5 block truncate text-[10px] text-gray-500 hover:underline dark:text-gray-400"
                          >
                            by {photo.user.name}
                          </a>
                        </div>
                      ))}
                    </div>

                    <p className="px-3 py-3 text-center text-xs text-faint">
                      Search to find more results
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <ImageCropModal {...coverCrop.cropModalProps} />
    </>
  );
}

